import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { activateAccount } from '../core/api/auth-service';
import { describeApiError } from '../core/errors/describe-api-error';
import { describeFieldErrors } from '../core/errors/describe-field-errors';
import type { TranslationKey } from '../core/i18n/translation';
import { useLanguage } from '../core/hooks/use-language';
import { useTranslation } from '../core/hooks/use-translation';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { AuthSurface } from './auth/auth-surface';
import { TextField } from './auth/text-field';

const MINIMUM_PASSWORD_LENGTH = 8;

/**
 * The screen a customer lands on from an invitation link, at `/:lang/account/activate`. The path
 * is fixed by `CustomerInvitationLink` on the server — change one and the other stops working.
 *
 * Activating does not sign the customer in: the server returns no session, so a link that has been
 * forwarded or intercepted still cannot open the portal on its own.
 */
export function ActivateAccountPage() {
    const { t } = useTranslation();
    const language = useLanguage();
    const [searchParams] = useSearchParams();

    const email = searchParams.get('email') ?? '';
    const token = searchParams.get('token') ?? '';

    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const linkIsComplete = email.length > 0 && token.length > 0;

    async function onSubmit(event: FormEvent) {
        event.preventDefault();
        setErrorKey(null);

        if (password.length < MINIMUM_PASSWORD_LENGTH) {
            setErrorKey('auth.activate.too_short');
            return;
        }

        if (password !== confirmation) {
            setErrorKey('auth.activate.mismatch');
            return;
        }

        setSubmitting(true);

        try {
            await activateAccount({ email, token, password });
            setDone(true);
        } catch (error) {
            // Only one field can be at fault here, so its message replaces the banner rather than
            // marking a box — a server rule the client does not mirror said "unexpected error".
            setErrorKey(describeFieldErrors(error)['password'] ?? describeApiError(error));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <AuthSurface>
            <h1 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100">
                {t('auth.activate.title')}
            </h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{t('auth.activate.subtitle')}</p>

            {!linkIsComplete && (
                <div className="mt-6">
                    <Alert tone="warning">{t('auth.activate.missing_link')}</Alert>
                </div>
            )}

            {done ? (
                <div className="mt-7 space-y-5">
                    <Alert tone="positive">{t('auth.activate.success')}</Alert>

                    <Link
                        to={`/${language}/account/sign-in`}
                        className="inline-flex w-full items-center justify-center rounded-xl bg-brand-600 px-5 py-3 font-semibold text-white transition hover:bg-brand-700"
                    >
                        {t('auth.sign_in.submit')}
                    </Link>
                </div>
            ) : (
                // The same field component the other two screens use. It was two hand-written
                // labels here, which is why this was the one account screen whose error message was
                // not tied to the box it belonged to.
                <form className="mt-7 space-y-4" onSubmit={(event) => void onSubmit(event)}>
                    <TextField
                        label={t('auth.activate.password')}
                        type="password"
                        value={password}
                        autoComplete="new-password"
                        hint={t('auth.activate.too_short')}
                        onChange={setPassword}
                    />

                    <TextField
                        label={t('auth.activate.confirm_password')}
                        type="password"
                        value={confirmation}
                        autoComplete="new-password"
                        onChange={setConfirmation}
                    />

                    {errorKey !== null && <Alert>{t(errorKey)}</Alert>}

                    <Button type="submit" size="lg" block disabled={submitting || !linkIsComplete}>
                        {submitting ? t('common.loading') : t('auth.activate.submit')}
                    </Button>
                </form>
            )}
        </AuthSurface>
    );
}
